'use client';

import type { ReactNode } from 'react';
import styles from './segmented-control.module.css';

/**
 * SegmentedControl - the inline 2-4 option toggle (design system). One bordered
 * group of segments with the active one highlighted; token-only, `sm`/`md` sizes.
 * Replaces the hand-rolled mode toggles (schedule Date/After, the activity filter).
 * Arrow keys move between segments; click / Enter / Space selects.
 * `disabled` draws the choice for a reader: the segments show the value and
 * take no press, in the kit's one disabled recipe.
 * `data-*` attributes pass through to the group, as on the kit `Chip`.
 */

/** `data-*` attributes pass through to the group. */
type SegmentedData = { [key: `data-${string}`]: string | number | boolean | undefined };

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  /** Native hover tooltip for the segment (e.g. when the label is terse and a
   *  fuller name helps). Falls back to nothing. */
  title?: string;
  /** The accessible name, REQUIRED when `label` is an icon rather than text
   *  (an icon-only segment names nothing on its own). Text segments name
   *  themselves and should leave this unset. */
  ariaLabel?: string;
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  size = 'md',
  fill = false,
  disabled = false,
  className,
  ...data
}: {
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  ariaLabel?: string;
  /** `sm` / `md` are the control heights; `xs` is the md chip's, for a control
   *  in a row of md chips (the criteria bar's all / any switch). */
  size?: 'xs' | 'sm' | 'md';
  /** Span the container: the segments share its width and a long label
   *  wraps, so three answers fit a phone's width (the agent level choice on
   *  /connect, 2026-10-06). */
  fill?: boolean;
  /** No segment takes a press: the value is read, not chosen. */
  disabled?: boolean;
  className?: string;
} & SegmentedData) {
  function onKeyDown(e: React.KeyboardEvent, i: number) {
    const dir = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!dir) return;
    e.preventDefault();
    const next = (i + dir + options.length) % options.length;
    onChange(options[next]!.value);
    const btns = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('button');
    btns?.[next]?.focus();
  }
  return (
    <div
      {...data}
      className={className ? `${styles.group} ${className}` : styles.group}
      role="group"
      aria-label={ariaLabel}
      data-size={size}
      data-fill={fill || undefined}
      data-disabled={disabled || undefined}
    >
      {options.map((o, i) => (
        <button
          key={o.value}
          type="button"
          className={styles.seg}
          title={o.title}
          aria-label={o.ariaLabel}
          data-on={o.value === value || undefined}
          aria-pressed={o.value === value}
          disabled={disabled || undefined}
          onClick={() => onChange(o.value)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
